import React, { useEffect, useState } from "react";
import ReactCropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import Particles from "react-tsparticles";
import { loadConfettiPreset } from "tsparticles-preset-confetti";
import { useNavigate } from "react-router-dom";

const api = import.meta.env.VITE_API_URL;

const UserForm = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(false);
  const [submit, setSubmit] = useState("Submit");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    photo: "",
    front_adhar: "",
    back_adhar: "",
    role: "1",
    style: "1",
    islocal: "1",
  });

  const [term, setTerm] = useState(null);

  const [imageData, setImageData] = useState({
    photo: null,
    front_adhar: null,
    back_adhar: null,
  });

  const [croppedImages, setCroppedImages] = useState({
    photo: null,
    front_adhar: null,
    back_adhar: null,
  });

  const [cropperInstances, setCropperInstances] = useState({
    photo: null,
    front_adhar: null,
    back_adhar: null,
  });

  const [activeField, setActiveField] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === "email" ? value.toLowerCase() : value,
    });
    setError(false);
  };

  const handleImageUpload = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () =>
        setImageData((prev) => ({ ...prev, [field]: reader.result }));
      reader.readAsDataURL(file);
      setActiveField(field);
    }
  };

  const handleCrop = (field) => {
    if (cropperInstances[field]) {
      const croppedCanvas = cropperInstances[field].getCroppedCanvas();
      const croppedImage = croppedCanvas.toDataURL("image/jpeg");
      setCroppedImages((prev) => ({ ...prev, [field]: croppedImage }));
      setFormData((prev) => ({ ...prev, [field]: croppedImage }));
      setActiveField(null);
    }
  };

  const handleReupload = (field) => {
    setImageData((prev) => ({ ...prev, [field]: null }));
    setCroppedImages((prev) => ({ ...prev, [field]: null }));
    setActiveField(field);
  };

  const particlesInit = async (main) => {
    await loadConfettiPreset(main);
  };

  const particlesOptions = {
    preset: "confetti",
    fullScreen: { enable: true, zIndex: 1 },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!term) {
      setError("Please accept our terms.");
      return;
    }
    if (!formData.name || !formData.phone || !formData.email || !formData.address) {
      setError("Please fill in all required details.");
      return;
    }
    if (!croppedImages.photo || !croppedImages.front_adhar || !croppedImages.back_adhar) {
      setError("Please upload all required images.");
      return;
    }

    const formDataToSend = new FormData();

    Object.keys(formData).forEach((key) => {
      formDataToSend.append(key, formData[key]);
    });

    if (croppedImages.photo) {
      const blob = await fetch(croppedImages.photo).then((res) => res.blob());
      formDataToSend.append("photo", blob, "photo.jpg");
    }
    if (croppedImages.front_adhar) {
      const blob = await fetch(croppedImages.front_adhar).then((res) => res.blob());
      formDataToSend.append("front_adhar", blob, "front_adhar.jpg");
    }
    if (croppedImages.back_adhar) {
      const blob = await fetch(croppedImages.back_adhar).then((res) => res.blob());
      formDataToSend.append("back_adhar", blob, "back_adhar.jpg");
    }

    try {
      setSubmit("Submitting...");
      const response = await fetch(`${api}/api/players`, {
        method: "POST",
        body: formDataToSend,
        headers: { Accept: "application/json" },
      });

      if (response.ok) {
        let data = await response.json();
        localStorage.setItem("player", data.id);
        setIsSubmitted(true);

        setTimeout(() => {
          navigate("/profile");
        }, 5000);
      } else {
        setSubmit("Retry");
      }
    } catch (error) {
      setSubmit("Retry");
      console.error("Error:", error);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-900 text-white px-4 my-4">
      {isSubmitted ? (
        <>
          <Particles id="tsparticles" init={particlesInit} options={particlesOptions} />
          <div className="text-center relative z-10">
            <h2 className="text-3xl sm:text-5xl font-bold mb-4">🎉 Player Registered Successfully!</h2>
            <p className="text-lg text-gray-300">Redirecting to your profile...</p>
          </div>
        </>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-lg bg-white/10 backdrop-blur-md p-6 sm:p-8 rounded-2xl shadow-xl relative z-10"
        >
          <h1 className="text-2xl sm:text-4xl font-bold text-center mb-6">Player Registration</h1>

          {/* Inputs */}
          {["name", "email", "phone", "address"].map((field) => (
            <div key={field} className="mb-4">
              <label className="block text-sm font-medium mb-1 capitalize">
                {field} <span className="text-rose-500">*</span>
              </label>
              {field === "address" ? (
                <textarea
                  name={field}
                  value={formData[field]}
                  onChange={handleInputChange}
                  placeholder={`Enter your ${field}`}
                  className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              ) : (
                <input
                  type={field === "email" ? "email" : field === "phone" ? "number" : "text"}
                  name={field}
                  value={formData[field]}
                  onChange={handleInputChange}
                  placeholder={`Enter your ${field}`}
                  className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              )}
            </div>
          ))}

          {/* Image Uploads */}
          {["photo", "front_adhar", "back_adhar"].map((field) => (
            <div key={field} className="mb-6">
              <label className="block text-sm font-medium mb-1">
                {field === "photo"
                  ? "Upload Photo"
                  : field === "front_adhar"
                  ? "Upload Aadhaar Front"
                  : "Upload Aadhaar Back"}
                <span className="text-rose-500">*</span>
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, field)}
                className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500"
              />

              {imageData[field] && activeField === field && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/70 z-50">
                  <div className="bg-gray-900 p-4 rounded-xl w-96 max-w-lg">
                    <ReactCropper
                      src={imageData[field]}
                      style={{ width: "100%", height: "auto" }}
                      aspectRatio={field === "photo" ? 1 : NaN}
                      viewMode={1}
                      guides
                      zoomable
                      cropBoxResizable
                      cropBoxMovable
                      onInitialized={(instance) =>
                        setCropperInstances((prev) => ({ ...prev, [field]: instance }))
                      }
                    />
                    <button
                      type="button"
                      onClick={() => handleCrop(field)}
                      className="w-full mt-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-700"
                    >
                      Crop Image
                    </button>
                  </div>
                </div>
              )}

              {croppedImages[field] && !activeField && (
                <div className="text-center mt-3">
                  <img
                    src={croppedImages[field]}
                    alt="Cropped"
                    className="w-auto h-40 object-cover rounded-lg mx-auto"
                  />
                  <button
                    type="button"
                    onClick={() => handleReupload(field)}
                    className="mt-3 px-4 py-2 bg-gray-600 rounded-lg hover:bg-gray-700"
                  >
                    Re-upload
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* Selects */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Role</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="1">Batsman</option>
              <option value="2">Wicket-Keeper</option>
              <option value="3">Batsman-Allrounder</option>
              <option value="4">Bowler-Allrounder</option>
              <option value="5">Bowler</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Style</label>
            <select
              name="style"
              value={formData.style}
              onChange={handleInputChange}
              className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="1">Right-Handed</option>
              <option value="2">Left-Handed</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Is Local?</label>
            <select
              name="islocal"
              value={formData.islocal}
              onChange={handleInputChange}
              className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="1">Local</option>
              <option value="0">Overseas</option>
            </select>
          </div>

          {/* Terms */}
          <div className="mb-4 flex items-center">
            <input
              type="checkbox"
              id="term"
              checked={term}
              onChange={() => {
                setTerm(!term);
                setError(false);
              }}
              className="mr-2"
            />
            <label htmlFor="term" className="text-sm">
              I confirm all details are correct <span className="text-rose-500">*</span>
            </label>
          </div>

          {error && <p className="text-rose-500 text-sm mb-4">{error}</p>}

          {/* Submit */}
          <button
            type="submit"
            className="w-full py-3 text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg shadow-lg hover:opacity-90 transition"
          >
            {submit}
          </button>
        </form>
      )}
    </div>
  );
};

export default UserForm;
